/*
  The population breakdown band (§9.1, §9.2, §9.3, D13, D26).

  Four figures — how much of the catalog exists, how much of it has been graded, and what
  is unaccounted for — on `/`, `/:gameCode` and `/:gameCode/:setName` and nowhere else.

  **Hidden is the state that matters.** The endpoint is D11's separate backend MR and
  returns 404 in production today, so until that merges this component renders `null` on
  every page load. Hidden therefore has to look intentional, not broken:

    - no skeleton. A placeholder that flashes and vanishes on every load is worse than
      nothing, and "every load" is what a permanent 404 means.
    - no error UI, no `console.error`, no `console.warn`. Chromium's own
      "Failed to load resource: 404" comes from the network layer and no application code
      can suppress it; nothing here adds to it (§12.23).
    - no layout shift. Returning `null` renders nothing at all, so the flex column that
      holds the band contributes no gap for it either.

  **A conforming 200 whose figures are all zero still renders** (§9.2). That is real data —
  a set with nothing graded — and hiding it would misreport. `parseBreakdown` is what draws
  that line and it is unit-tested; this file only adapts RTK Query's result into its two
  arguments.

  **A scoped band with no id is hidden, never unscoped** (§9.2, §12.25). `SetView` resolves
  `setId` from a `limit: 200` lookup, so a set beyond the 200th has none. Firing the
  unscoped request instead would put the site-wide figures under a set heading, which is
  not a degraded answer but a wrong one. `skip` is how that is enforced: no id, no request.

  **Nothing here presents a figure as part of a whole** (§9.3) — no stacked bar, no
  percentage-of-total, no "N of M". Per-game `manuallyEntered` is a best-effort string
  match because manually entered cards have no catalog link by definition, so the scoped
  figures do not sum to the global ones and must not look as though they should. When the
  server says `approximate`, the figure carries the ≈ marker that says so.
*/

import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import { formatCount, parseBreakdown } from 'lib';
import type { BreakdownFigures, BreakdownScope, BreakdownState } from 'lib';

import { useGetCatalogBreakdownQuery } from 'services/pcgPublicV2';

/* D26 fixes these four labels; the catalog-link concept stays out of the public copy. */
const FIELDS: { key: keyof Omit<BreakdownFigures, 'scope' | 'approximate'>; label: string }[] = [
  { key: 'inCatalog', label: 'Cards in catalog' },
  { key: 'gradedLinked', label: 'Graded' },
  { key: 'manuallyEntered', label: 'Entered manually' },
  { key: 'inGrading', label: 'In grading' },
];

/* §9.3's sentence, verbatim. It is both the tooltip and the marker's accessible name. */
export const APPROXIMATE_LABEL =
  'Approximate — manually entered cards have no catalog link, so they are matched by name';

/* Only the string-matched figure is approximate; the other three are exact either way. */
const APPROXIMATE_FIELD = 'manuallyEntered';

/*
  RTK Query's result -> parseBreakdown's two arguments. Exported and unit-tested because
  it is the half of the band that can be wrong: component tests do not run here (RTL 13
  against React 17, §2).

  **The status passed on failure is `originalStatus ?? status`.** Production's 404 serves
  an HTML error page, and fetchBaseQuery's JSON response handler cannot parse it, so what
  arrives is `{ status: 'PARSING_ERROR', originalStatus: 404 }` — the real HTTP status is
  the one underneath. Passing `status` alone would hand parseBreakdown the string
  'PARSING_ERROR', which it hides anyway, so the band would look right for the wrong
  reason and a 200 with a malformed body would be indistinguishable from a 404.

  With no error, the body reached the client and fetchBaseQuery parsed it, which is a 2xx
  by construction. Loading and skipped both arrive here as two undefineds and hide.
*/
export const breakdownStateFromQuery = (
  data: unknown,
  error: FetchBaseQueryError | SerializedError | undefined
): BreakdownState => {
  if (error) {
    const status = 'status' in error ? error.status : undefined;
    const originalStatus = 'originalStatus' in error ? error.originalStatus : undefined;
    const body = 'data' in error ? error.data : undefined;
    return parseBreakdown(originalStatus ?? status, body);
  }
  return parseBreakdown(200, data);
};

export interface PopulationBreakdownProps {
  /* Which of §9.1's three levels this mount is. */
  scope: BreakdownScope;
  /* The id that level needs. Undefined on a scoped band means hidden, not unscoped. */
  scopeId?: number;
}

const CARD =
  'skin-surface rounded-lg border border-pcg-border bg-pcg-card p-4 sm:p-5';

const FIELD_LABEL =
  'text-xs font-semibold uppercase tracking-wide text-pcg-textMuted';

/* tabular-nums so the four figures line up; formatCount pins the locale to en-US (§6.2). */
const FIELD_VALUE =
  'skin-statband-figure flex items-baseline gap-1 text-xl font-semibold tabular-nums text-pcg-text sm:text-2xl';

const PopulationBreakdown = (props: PopulationBreakdownProps) => {
  const { scope, scopeId } = props;

  const unresolvedScope = scope !== 'global' && scopeId === undefined;

  /*
    `currentData`, not `data`: RTK retains the last successful result across an arg change,
    so navigating game A -> game B would paint A's four figures under B's heading until B
    resolved. §9.2 forbids exactly that. `currentData` is undefined for the pending arg, so
    the band hides rather than lying.
  */
  const { currentData: data, error } = useGetCatalogBreakdownQuery(
    {
      gameId: scope === 'game' ? scopeId : undefined,
      setId: scope === 'set' ? scopeId : undefined,
    },
    /* No id, no request — §12.25 asserts that nothing is sent, not merely that the band is absent. */
    { skip: unresolvedScope }
  );

  const state = breakdownStateFromQuery(data, error);

  if (!state.available) {
    return null;
  }

  const { figures } = state;

  return (
    <section
      aria-labelledby="population-breakdown-heading"
      className={CARD}
    >
      <h2
        id="population-breakdown-heading"
        className="font-display text-lg font-semibold text-pcg-text"
      >
        Population breakdown
      </h2>

      <dl className="skin-statband mt-3 grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
        {FIELDS.map(field => (
          <div key={field.key} className="flex min-w-0 flex-col gap-1">
            <dt className={FIELD_LABEL}>{field.label}</dt>
            <dd className={FIELD_VALUE}>
              {formatCount(figures[field.key])}
              {figures.approximate && field.key === APPROXIMATE_FIELD ? (
                /*
                  role="img" + aria-label gives the marker an accessible name of its own
                  rather than reading out as the character; title is the tooltip. Not
                  focusable, so it needs no focus ring.
                */
                <span
                  role="img"
                  aria-label={APPROXIMATE_LABEL}
                  title={APPROXIMATE_LABEL}
                  className="text-base font-normal text-pcg-textMuted"
                >
                  ≈
                </span>
              ) : null}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
};

export default PopulationBreakdown;
